import React from "react";

interface MarkdownProps {
    children: string;
    className?: string;
    inverted?: boolean;
}

const escapeHtml = (value: string): string => {
    return value
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;");
};

const renderInline = (value: string, inverted: boolean = false): string => {
    let result = escapeHtml(value);

    // Inline code `code`
    result = result.replace(
        /`([^`]+)`/g,
        inverted
            ? '<code class="bg-white/20 text-white px-1.5 py-0.5 rounded text-[11px] font-mono">$1</code>'
            : '<code class="bg-slate-100 dark:bg-white/10 text-slate-800 dark:text-zinc-200 px-1.5 py-0.5 rounded text-[11px] font-mono">$1</code>'
    );

    // Bold **text**
    result = result.replace(
        /\*\*([^*]+)\*\*/g,
        '<strong class="font-bold">$1</strong>'
    );

    // Italic _text_ or *text*
    result = result.replace(/_([^_]+)_/g, '<em class="italic">$1</em>');

    const linkBtnClasses = inverted
        ? "text-amber-200 hover:text-amber-100 dark:text-amber-200 no-underline hover:no-underline font-semibold cursor-pointer bg-transparent border-0 p-0 inline align-baseline transition-colors"
        : "text-primary dark:text-primary-400 hover:text-primary-hover dark:hover:text-primary-300 no-underline hover:no-underline font-semibold cursor-pointer bg-transparent border-0 p-0 inline align-baseline transition-colors";

    const linkAnchorClasses = inverted
        ? "text-amber-200 hover:text-amber-100 dark:text-amber-200 no-underline hover:no-underline font-semibold transition-colors"
        : "text-primary dark:text-primary-400 hover:text-primary-hover dark:hover:text-primary-300 no-underline hover:no-underline font-semibold transition-colors";

    // Attachment mentions @fichero:name, @file:name, @fichero:"name with spaces", etc.
    result = result.replace(
        /@(fichero|file|fitxer|adjunto|attachment):(?:"([^"]+)"|([^\s,;!?:()<>[\]"]+))/gi,
        (
            _match,
            prefix: string,
            quotedName?: string,
            unquotedName?: string
        ) => {
            const name = (quotedName || unquotedName || "").trim();
            const escapedName = escapeHtml(name);
            return `<button type="button" data-attachment-name="${escapedName}" class="${linkBtnClasses}">${escapedName}</button>`;
        }
    );

    // Links [text](url) and legacy attachment refs [text](attachment:id)
    result = result.replace(
        /\[([^\]]+)\]\(attachment:(\d+)\)/g,
        `<button type="button" data-attachment-id="$2" class="${linkBtnClasses}">$1</button>`
    );
    result = result.replace(
        /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
        `<a href="$2" target="_blank" rel="noopener noreferrer" class="${linkAnchorClasses}">$1</a>`
    );

    return result;
};

export const Markdown: React.FC<MarkdownProps> = ({
    children,
    className = "",
    inverted = false,
}: MarkdownProps) => {
    if (!children) {
        return null;
    }

    const isWhite =
        inverted ||
        className.includes("text-white") ||
        className.includes("bg-primary");

    if (
        typeof document !== "undefined" &&
        /<\/?(?:p|h[1-6]|ul|ol|li|table|blockquote|pre|code|strong|em|u|s|a|img)\b/i.test(
            children
        )
    ) {
        const parser = new DOMParser();
        const parsed = parser.parseFromString(children, "text/html");
        const allowedTags = new Set([
            "P",
            "BR",
            "STRONG",
            "EM",
            "U",
            "S",
            "H1",
            "H2",
            "H3",
            "H4",
            "UL",
            "OL",
            "LI",
            "BLOCKQUOTE",
            "PRE",
            "CODE",
            "TABLE",
            "THEAD",
            "TBODY",
            "TR",
            "TH",
            "TD",
            "A",
            "IMG",
            "HR",
            "SPAN",
            "BUTTON",
        ]);
        parsed.body.querySelectorAll("*").forEach((element) => {
            if (!allowedTags.has(element.tagName)) {
                element.replaceWith(...Array.from(element.childNodes));
                return;
            }
            Array.from(element.attributes).forEach((attribute) => {
                const allowed = [
                    "href",
                    "target",
                    "rel",
                    "src",
                    "alt",
                    "title",
                    "colspan",
                    "rowspan",
                    "style",
                    "data-attachment-id",
                    "data-attachment-name",
                    "type",
                    "class",
                ];
                if (
                    !allowed.includes(attribute.name) ||
                    /^javascript:/i.test(attribute.value)
                ) {
                    element.removeAttribute(attribute.name);
                }
            });
            if (element.tagName === "A") {
                element.setAttribute("target", "_blank");
                element.setAttribute("rel", "noopener noreferrer");
            }
        });

        const proseClasses = isWhite
            ? "markdown-content richtext-editor prose prose-sm max-w-none text-white [&_*]:!text-white [&_a]:!text-amber-200 [&_a]:!no-underline hover:[&_a]:!text-amber-100 hover:[&_a]:!no-underline [&_button]:!text-amber-200 [&_button]:!no-underline hover:[&_button]:!text-amber-100 hover:[&_button]:!no-underline [&_code]:!bg-white/20 [&_code]:!text-white"
            : "markdown-content richtext-editor prose prose-sm max-w-none [&_a]:!no-underline [&_a]:text-primary dark:[&_a]:text-primary-400 hover:[&_a]:text-primary-hover hover:[&_a]:!no-underline [&_button]:!no-underline hover:[&_button]:!no-underline";

        return (
            <div
                className={`${proseClasses} ${className}`}
                dangerouslySetInnerHTML={{ __html: parsed.body.innerHTML }}
            />
        );
    }

    const lines = children.split("\n");
    const elements: string[] = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];

        // Code block ```
        if (line.trim().startsWith("```")) {
            const codeLines: string[] = [];
            i++;
            while (i < lines.length && !lines[i].trim().startsWith("```")) {
                codeLines.push(escapeHtml(lines[i]));
                i++;
            }
            i++; // skip closing ```
            elements.push(
                `<pre class="bg-slate-900 text-slate-100 p-3.5 rounded-xl overflow-x-auto my-2 text-xs font-mono leading-relaxed"><code>${codeLines.join("\n")}</code></pre>`
            );
            continue;
        }

        // Table check: starts with | and next line has | --- |
        if (line.trim().startsWith("|") && line.trim().endsWith("|")) {
            const tableLines: string[] = [];
            while (
                i < lines.length &&
                lines[i].trim().startsWith("|") &&
                lines[i].trim().endsWith("|")
            ) {
                tableLines.push(lines[i].trim());
                i++;
            }

            if (tableLines.length >= 2 && tableLines[1].includes("---")) {
                const parseRow = (rowStr: string): string[] => {
                    return rowStr
                        .slice(1, -1)
                        .split("|")
                        .map((cell) => cell.trim());
                };

                const headers = parseRow(tableLines[0]);
                const theadHtml = headers
                    .map(
                        (h) =>
                            `<th class="border border-slate-200 dark:border-white/20 bg-slate-100 dark:bg-white/10 px-3 py-2 text-left font-bold text-xs ${isWhite ? "text-white" : "text-slate-800 dark:text-zinc-100"}">${renderInline(h, isWhite)}</th>`
                    )
                    .join("");

                const bodyRowsHtml = tableLines
                    .slice(2)
                    .map((rowStr) => {
                        const cells = parseRow(rowStr);
                        const cellsHtml = cells
                            .map(
                                (c) =>
                                    `<td class="border border-slate-200 dark:border-white/10 px-3 py-1.5 text-xs ${isWhite ? "text-white" : "text-slate-700 dark:text-zinc-200"}">${renderInline(c, isWhite)}</td>`
                            )
                            .join("");
                        return `<tr class="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">${cellsHtml}</tr>`;
                    })
                    .join("");

                elements.push(
                    `<div class="overflow-x-auto my-2 rounded-xl border border-slate-200 dark:border-white/15 shadow-xs"><table class="min-w-full divide-y divide-slate-200 dark:divide-white/10 border-collapse"><thead><tr>${theadHtml}</tr></thead><tbody class="divide-y divide-slate-100 dark:divide-white/5">${bodyRowsHtml}</tbody></table></div>`
                );
                continue;
            } else {
                // If not standard table, render as normal divs
                for (const tl of tableLines) {
                    elements.push(`<div>${renderInline(tl, isWhite)}</div>`);
                }
                continue;
            }
        }

        // Heading #, ##, ###
        const heading = line.match(/^(#{1,4})\s+(.+)$/);
        if (heading) {
            const level = heading[1].length;
            const headingClasses: Record<number, string> = isWhite
                ? {
                      1: "text-base font-black text-white mt-3 mb-1.5",
                      2: "text-sm font-bold text-white mt-2.5 mb-1",
                      3: "text-xs font-bold text-white mt-2 mb-0.5",
                      4: "text-xs font-semibold text-white/90 mt-1.5 mb-0.5",
                  }
                : {
                      1: "text-base font-black text-slate-900 dark:text-white mt-3 mb-1.5",
                      2: "text-sm font-bold text-slate-800 dark:text-zinc-100 mt-2.5 mb-1",
                      3: "text-xs font-bold text-slate-800 dark:text-zinc-200 mt-2 mb-0.5",
                      4: "text-xs font-semibold text-slate-700 dark:text-zinc-300 mt-1.5 mb-0.5",
                  };
            elements.push(
                `<h${level} class="${headingClasses[level] || ""}">${renderInline(heading[2], isWhite)}</h${level}>`
            );
            i++;
            continue;
        }

        // Blockquote >
        const quote = line.match(/^>\s*(.+)$/);
        if (quote) {
            const bqClasses = isWhite
                ? "border-l-4 border-white/60 bg-white/10 pl-3 py-1 my-1.5 rounded-r-lg text-xs italic text-white"
                : "border-l-4 border-primary/60 bg-primary/5 dark:bg-white/5 pl-3 py-1 my-1.5 rounded-r-lg text-xs italic text-slate-700 dark:text-zinc-300";
            elements.push(
                `<blockquote class="${bqClasses}">${renderInline(quote[1], isWhite)}</blockquote>`
            );
            i++;
            continue;
        }

        // Bullet list - or *
        const bullet = line.match(/^[-*]\s+(.+)$/);
        if (bullet) {
            const bulletDotClass = isWhite
                ? "text-white/80 font-bold"
                : "text-primary font-bold";
            const bulletTextClass = isWhite
                ? "flex-1 text-xs text-white"
                : "flex-1 text-xs text-slate-800 dark:text-zinc-200";
            elements.push(
                `<div class="flex items-start gap-2 my-0.5"><span class="${bulletDotClass}">•</span><span class="${bulletTextClass}">${renderInline(bullet[1], isWhite)}</span></div>`
            );
            i++;
            continue;
        }

        // Numbered list 1. 2.
        const numbered = line.match(/^(\d+)\.\s+(.+)$/);
        if (numbered) {
            const numClass = isWhite
                ? "font-bold text-white/80 text-xs"
                : "font-bold text-slate-500 text-xs";
            const numTextClass = isWhite
                ? "flex-1 text-xs text-white"
                : "flex-1 text-xs text-slate-800 dark:text-zinc-200";
            elements.push(
                `<div class="flex items-start gap-2 my-0.5"><span class="${numClass}">${numbered[1]}.</span><span class="${numTextClass}">${renderInline(numbered[2], isWhite)}</span></div>`
            );
            i++;
            continue;
        }

        // Empty line
        if (!line.trim()) {
            elements.push('<div class="h-2"></div>');
            i++;
            continue;
        }

        // Normal text line
        const textClass = isWhite
            ? "text-xs text-white leading-relaxed"
            : "text-xs text-slate-800 dark:text-zinc-200 leading-relaxed";
        elements.push(
            `<div class="${textClass}">${renderInline(line, isWhite)}</div>`
        );
        i++;
    }

    return (
        <div
            className={`markdown-content space-y-0.5 ${className}`}
            dangerouslySetInnerHTML={{ __html: elements.join("") }}
        />
    );
};

export default Markdown;
